import { Injectable } from '@angular/core';

@Injectable()
export class ServiceCombinationBuilderService {

    constructor() { }

    /**
     * Creates a new empty combination object based on available vehicles from origin point
     * @param originPoint Origin point with available camionetas
     * @returns New combination object with all vehicle types initialized to 0
     */
    createEmptyCombination(originPoint: any): any {
        let new_combinacion = {};
        
        if (originPoint && originPoint.camionetas) {
            originPoint.camionetas.forEach(vehicle => {
                new_combinacion[vehicle.nombre] = 0;
            });
        }
        
        return new_combinacion;
    }

    /**
     * Calculates the percentage to apply based on total service hours
     * Business Rule:
     * - 1-10 hours: 30% + 8% per hour after first hour
     * - >10 hours: 102% + 4.12% per hour after 10 hours
     * @param totalHours Total hours of service (outbound + return)
     * @returns Percentage to apply to fixed cost
     */
    calculateHourlyPercentage(totalHours: number): number {
        if (totalHours >= 1 && totalHours <= 10) {
            return 30 + (8 * (totalHours - 1));
        } else if (totalHours > 10) {
            return 102 + (4.12 * (totalHours - 10));
        }
        return 0;
    }

    /**
     * Calculates the cost for a single vehicle in a combination
     * @param params Calculation parameters
     * @returns Total cost for the vehicle
     */
    calculateVehicleCost(params: {
        vehicle: any,
        distanciaOrigen: number,
        kilometrosGoogle: number,
        distanciaTrayectoRegreso: number,
        distanciaTrayectoRegresoOrigen: number,
        totalHours: number
    }): number {
        let total_camionetas = 0;
        const { vehicle, distanciaOrigen, kilometrosGoogle, distanciaTrayectoRegreso, distanciaTrayectoRegresoOrigen, totalHours } = params;

        // Apply 10km free threshold for origin distance
        if (distanciaOrigen > 10) {
            total_camionetas += (distanciaOrigen - 10) * vehicle.costo_km / 1;
        }

        // Add main route cost
        total_camionetas += vehicle.costo_km / 1 * kilometrosGoogle;

        // Add return trip cost
        total_camionetas += vehicle.costo_km / 1 * distanciaTrayectoRegreso;

        // Add return to origin cost (with 10km threshold)
        total_camionetas += (distanciaTrayectoRegresoOrigen - 10) * vehicle.costo_km / 1;

        // Apply hourly percentage to fixed cost
        const percentage = this.calculateHourlyPercentage(totalHours);
        if (percentage > 0) {
            total_camionetas += vehicle.costo_fijo / 1 * percentage / 100;
        }

        return total_camionetas;
    }

    /**
     * Calculates the total price for a single combination
     * @param params Calculation parameters
     * @returns Price details for the combination
     */
    calculateCombinationPrice(params: {
        combination: any,
        vehicles: any[],
        distanciaOrigen: number,
        kilometrosGoogle: number,
        distanciaTrayectoRegreso: number,
        distanciaTrayectoRegresoOrigen: number,
        totalHours: number,
        totalComidasOperador: number,
        totalHospedajeOperador: number,
        cantidadOperadores: number
    }): { total: number, percentage: number } {
        let totalizado_combinacion = 0;
        let lastPercentage = 0;
        const { combination, vehicles, distanciaOrigen, kilometrosGoogle, distanciaTrayectoRegreso, 
                distanciaTrayectoRegresoOrigen, totalHours, totalComidasOperador, totalHospedajeOperador, 
                cantidadOperadores } = params;

        vehicles.forEach(vehicle => {
            if (combination[vehicle.nombre] > 0) {
                const vehicleCost = this.calculateVehicleCost({
                    vehicle,
                    distanciaOrigen,
                    kilometrosGoogle,
                    distanciaTrayectoRegreso,
                    distanciaTrayectoRegresoOrigen,
                    totalHours
                });

                const operatorCosts = (totalHospedajeOperador * combination[vehicle.nombre] * cantidadOperadores / 1)
                                    + (totalComidasOperador * combination[vehicle.nombre] * cantidadOperadores / 1);

                totalizado_combinacion += (vehicleCost * combination[vehicle.nombre]) + operatorCosts;
                
                lastPercentage = this.calculateHourlyPercentage(totalHours);
            }
        });

        // Round up to nearest 50
        totalizado_combinacion = Math.ceil(totalizado_combinacion / 50) * 50;

        return {
            total: totalizado_combinacion,
            percentage: lastPercentage
        };
    }

    /**
     * Builds a price combination object
     * @param combination The vehicle combination
     * @param total Total calculated price
     * @returns Price combination object
     */
    buildPriceCombination(combination: any, total: number): any {
        return {
            combinacion: combination,
            total_camionetas: total,
            total_adicionales: 0,
            total: total,
            descuentoAQuitarAdmin: 0,
            descuentoAQuitarMark: 0,
            descuentoAQuitarAdicional: 0,
            precioConDescuento: total
        };
    }

    /**
     * Applies the FIFA World Cup 2026 surcharge to the vehicle cost total.
     * Active from June 1, 2026 to July 5, 2026.
     * Applies when any part of the service overlaps with the World Cup period.
     * - Services < 48 hours: 10% surcharge
     * - Services >= 48 hours: 5% surcharge
     * @param total The base total_camionetas price
     * @param totalHours Total service hours (outbound + return)
     * @param serviceStartDate Service start date as timestamp in milliseconds
     * @param serviceEndDate Service end date as timestamp in milliseconds
     * @returns Adjusted total with surcharge applied (rounded to nearest 50), or original total if outside date range
     */
    calculateWorldCupSurcharge(total: number, totalHours: number, serviceStartDate: number, serviceEndDate: number): number {
        var worldCupStart = new Date(2026, 5, 1, 0, 0, 0).getTime();
        var worldCupEnd = new Date(2026, 6, 5, 23, 59, 59).getTime();

        var overlaps = serviceStartDate <= worldCupEnd && serviceEndDate >= worldCupStart;

        if (overlaps) {
            var surchargeRate = totalHours < 48 ? 1.10 : 1.05;
            var adjustedTotal = total * surchargeRate;
            return Math.ceil(adjustedTotal / 50) * 50;
        }

        return total;
    }

    /**
     * Validates if at least one unit is selected in combinations
     * @param combinations Array of combinations to validate
     * @returns True if valid, false otherwise
     */
    validateCombinations(combinations: any[]): boolean {
        if (!combinations || combinations.length === 0) {
            return false;
        }

        // Check if at least one combination has at least one vehicle selected
        for (let combination of combinations) {
            let hasVehicle = false;
            for (let key in combination) {
                if (combination[key] > 0) {
                    hasVehicle = true;
                    break;
                }
            }
            if (hasVehicle) {
                return true;
            }
        }

        return false;
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/service-combination-builder.service.ts